import React, { forwardRef, useState, useMemo, useEffect, useCallback, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import SydModalActions from '../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../commonDesign/Button'
import Loading from '../../../molecules/Loading'
import usePipelineQuery from '../../DenaliDataPipeline/hooks/usePipelineQuery'
import FieldGroup from './FieldGroup'
import { buildDefaultValues, normalizeFormDefaults, sortByOrdinal, coerceFormValues, getAllFields, buildArgsFromMapping, extractActionError } from './utils/fieldUtils'

const DynamicActionForm = forwardRef(function DynamicActionForm ({ actionTemplate, onSubmit, onCancel, subject, variables }, ref) {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)

  const {
    groups,
    dataPipelineCode,
    argMapping
  } = useMemo(() => ({
    groups: actionTemplate?.definition?.fieldMapping?.groups || [],
    dataPipelineCode: actionTemplate?.definition?.dataPipelineCode,
    argMapping: actionTemplate?.definition?.argMapping
  }), [actionTemplate])

  const initDisabled = !dataPipelineCode
  const { data: initResult, isLoading: initLoading } = usePipelineQuery(
    dataPipelineCode,
    {
      ...(variables || {}),
      subject
    },
    initDisabled
  )

  const defaultValues = useMemo(() => {
    if (!initDisabled && initLoading) {
      return { ...(variables || {}) }
    }
    const built = buildDefaultValues(groups, {
      ...(variables || {}),
      ...(initResult || {})
    })
    return normalizeFormDefaults(built, groups)
  }, [groups, initResult, initDisabled, initLoading, variables])

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onSubmit',
    criteriaMode: 'all',
    defaultValues
  })

  useEffect(() => {
    if (Object.keys(defaultValues).length > 0) {
      form.reset(defaultValues)
    }
  }, [defaultValues, form])

  const sortedGroups = useMemo(() => sortByOrdinal(groups), [groups])
  const allFields = useMemo(() => getAllFields(groups), [groups])

  const handleSubmit = useCallback(async (data) => {
    setProcessing(true)
    setError(null)

    try {
      const coercedData = coerceFormValues(data, allFields, groups)
      const args = buildArgsFromMapping(coercedData, argMapping)
      const result = await onSubmit(args)
      const actionError = extractActionError(result)

      if (actionError) {
        setError(actionError)
      } else {
        form.reset()
      }
    } catch (err) {
      setError(err.message || 'An error occurred while submitting the form.')
    } finally {
      setProcessing(false)
    }
  }, [allFields, groups, argMapping, onSubmit, form])

  const handleCancel = useCallback((e) => {
    setError(null)
    onCancel(e)
  }, [onCancel])

  useImperativeHandle(ref, () => ({ cancel: handleCancel }), [handleCancel])

  if (!initDisabled && initLoading) {
    return <Loading />
  }

  return (
    <>
      {sortedGroups.map((group, index) => (
        <FieldGroup
          key={group.id || `group-${index}`}
          group={group}
          control={form.control}
          form={form}
          initializationData={{ ...(variables || {}), ...(initResult || {}) }}
        />
      ))}

      <SydModalActions>
        {error && <div className='__error'>{error}</div>}
        <SydButton
          variant='ghost'
          onClick={handleCancel}
          disabled={processing}
        >
          Cancel
        </SydButton>
        <SydButton
          variant='primary'
          onClick={form.handleSubmit(handleSubmit)}
          processing={processing}
          disabled={!form.formState.isValid}
        >
          Submit
        </SydButton>
      </SydModalActions>
    </>
  )
})

DynamicActionForm.propTypes = {
  actionTemplate: PropTypes.shape({
    definition: PropTypes.shape({
      dataPipelineCode: PropTypes.string,
      fieldMapping: PropTypes.shape({
        groups: PropTypes.arrayOf(PropTypes.object)
      }),
      argMapping: PropTypes.object
    })
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  subject: PropTypes.any,
  variables: PropTypes.object
}

export default DynamicActionForm
